import React from 'react';
import PropTypes from 'prop-types';
import { Box, IconButton, Typography } from '@mui/material';
import i18n from '../../../../i18n';
import Iconify from '../../../../components/iconify';
import FilterItemsAbstract from '../../../../pages/FilterAbstarct/FilterItemsAbstract';
import Fields from '../../../../pages/FilterAbstarct/fields';
import { validateRange } from '../../../../utils/dateRangeHelper';
import {
    dateFieldSx,
    dateHelperSx,
    fieldSx,
    listSx,
    sectionLabelSx
} from './GridMobileFilterList.styles';

// The date range as a row of the sheet. The grid owns the range and its
// picker dialog; this shows it, opens the picker and clears it.
function MobileDateRange({
    value,
    onOpenPicker,
    onClear,
    disabled,
    disabledText,
    limitDateRange
}) {
    const isInvalid = !!value && !validateRange(value, limitDateRange);
    const helperText =
        disabledText ||
        (isInvalid
            ? i18n.t('text.dateRangeMax', { count: limitDateRange })
            : null);

    return (
        <Box>
            <Typography sx={sectionLabelSx}>{i18n.t('text.dateRange')}</Typography>
            <Box
                component="button"
                type="button"
                disabled={disabled}
                sx={dateFieldSx(!!value, disabled)}
                onClick={onOpenPicker}
            >
                <span>{value || i18n.t('text.dateRangePlaceholder')}</span>
                {value && !disabled ? (
                    <IconButton
                        component="span"
                        size="small"
                        edge="end"
                        aria-label={i18n.t('mobile.clearDateRange')}
                        onClick={(event) => {
                            event.stopPropagation();
                            onClear();
                        }}
                    >
                        <Iconify icon="eva:close-fill" />
                    </IconButton>
                ) : (
                    <Iconify icon="eva:calendar-outline" />
                )}
            </Box>
            {helperText ? (
                <Typography sx={dateHelperSx(isInvalid)}>{helperText}</Typography>
            ) : null}
        </Box>
    );
}

MobileDateRange.propTypes = {
    disabled: PropTypes.bool,
    disabledText: PropTypes.string,
    limitDateRange: PropTypes.number,
    onClear: PropTypes.func.isRequired,
    onOpenPicker: PropTypes.func.isRequired,
    value: PropTypes.string
};

// The grid's filters in the phone's filter sheet: the toolbar's filter fields
// and its date range, one under the other at full width. Which fields show
// and how a change becomes a filter are the toolbar's (FilterItemsAbstract).
export default class GridMobileFilterList extends FilterItemsAbstract {
    render() {
        const {
            Head,
            filters,
            params,
            showDatePicker,
            dateRangeValue,
            onOpenDateRange,
            onClearDateRange,
            disableDateRange,
            disableDateRangeText,
            limitDateRange
        } = this.props;

        return (
            <Box sx={listSx}>
                {showDatePicker && (
                    <MobileDateRange
                        value={dateRangeValue}
                        onOpenPicker={onOpenDateRange}
                        onClear={onClearDateRange}
                        disabled={disableDateRange}
                        disabledText={disableDateRangeText}
                        limitDateRange={limitDateRange}
                    />
                )}
                {Head.filter(
                    (field) =>
                        field.filterCell &&
                        this.allowedPermission('read', field.from)
                ).map((field) => {
                    const FieldComponent = Fields[field.filterCell];
                    if (!FieldComponent) return null;
                    return (
                        <Box key={field.filterBy || field.id} sx={fieldSx}>
                            <FieldComponent
                                field={field}
                                handleFilter={this.handleFilterChange}
                                param={params?.id}
                                filters={filters}
                                maxWidth="100%"
                            />
                        </Box>
                    );
                })}
            </Box>
        );
    }
}

GridMobileFilterList.propTypes = {
    ...FilterItemsAbstract.propTypes,
    dateRangeValue: PropTypes.string,
    disableDateRange: PropTypes.bool,
    disableDateRangeText: PropTypes.string,
    limitDateRange: PropTypes.number,
    onClearDateRange: PropTypes.func.isRequired,
    onOpenDateRange: PropTypes.func.isRequired,
    showDatePicker: PropTypes.bool
};
